/**
 * Plan Constants - Plan IDs and Helper Functions
 * 
 * This file contains plan IDs and helper functions for working with plans.
 * The actual plan data comes from the backend API as the single source of truth.
 */
import api from '../api';

// Plan IDs - used for API calls and routing
export const PLAN_IDS = {
  FREE: 'free',
  BASIC: 'basic',
  PRO: 'pro',
  AGENCY: 'agency',
  ENTERPRISE: 'enterprise'
};

// Plan Display Names - for quick reference without API call
export const PLAN_NAMES = {
  [PLAN_IDS.FREE]: 'Free',
  [PLAN_IDS.BASIC]: 'Basic',
  [PLAN_IDS.PRO]: 'Pro',
  [PLAN_IDS.AGENCY]: 'Agency',
  [PLAN_IDS.ENTERPRISE]: 'Enterprise'
};

// Store for plan details fetched from API
let PLAN_DETAILS = {};

// Get plan details with API integration
const getPlanDetails = async () => {
  try {
    // console.log('🔄 Fetching plan details from API...');
    
    const response = await api.payments.getPlans();
    // console.log('📦 Raw API response:', response);
    // console.log('📊 Response data:', response.data);
    
    // Handle different response formats
    let plans;
    if (response.data?.plans) {
      plans = response.data.plans;
    } else if (Array.isArray(response.data)) {
      plans = response.data;
      // console.log('📋 Extracted plans:', plans);
      // console.log('🔍 Plans is array?', Array.isArray(plans));
    } else {
      throw new Error('Unexpected API response format');
    }
    
    if (!Array.isArray(plans)) {
      throw new Error('Plans data is not an array');
    }
    
    // console.log(`✅ Processing ${plans.length} plans`);
    
    // Process each plan from API
    const apiPlanDetails = {};
    plans.forEach((plan, index) => {
      // console.log(`📝 Processing plan ${index + 1}:`, plan);
      
      const planId = plan.id || plan.plan_id;
      if (!planId) {
        return; // Skip plans without ID
      }
      
      apiPlanDetails[planId] = {
        id: planId,  // Add missing id property
        name: plan.name || 'Unknown Plan',
        price: plan.price_amount || plan.price || 0,  // Backend sends price_amount
        features: plan.features || [],
        limits: plan.limits || {},
        popular: plan.popular || false,  // Backend already sends 'popular'
        isPopular: plan.popular || false,
        stripePriceId: plan.price_id || plan.stripe_price_id || null,  // Backend sends price_id
        stripeAnnualPriceId: plan.stripe_annual_price_id || null,
        // Add properties expected by Pricing component - use direct field names from backend
        monthlyFiles: plan.max_files_per_month || 0,
        totalProducts: plan.total_products_per_month || 0,
        maxFileSize: plan.max_file_size_mb || 0,
        apiAccess: plan.enables_api_access || false,
        idealFor: plan.ideal_for || plan.description || ''
      };
      
      // console.log(`✅ Processed plan ${planId}:`, apiPlanDetails[planId]);
    });
    
    // console.log('🎉 Final plan details:', apiPlanDetails);
    // console.log('📊 Plan count:', Object.keys(apiPlanDetails).length);
    
    return apiPlanDetails;
  } catch (error) {
    console.error('❌ Failed to fetch plan details from API:', error);
    // Return empty object if API fails - let components handle the error
    return {};
  }
};

// Initialize by fetching plans (don't await - let it happen in background)
getPlanDetails();

// Export the plan details (this will initially be empty, then updated by API)
export { PLAN_DETAILS };

// Export the function to fetch plan details
export const fetchPlanDetails = getPlanDetails;

// Check if a plan is a paid plan
export const isPaidPlan = (planNameOrId) => {
  // If it's a plan ID
  if (Object.values(PLAN_IDS).includes(planNameOrId)) {
    return planNameOrId !== PLAN_IDS.FREE;
  }
  
  // If it's a plan name
  return planNameOrId !== PLAN_NAMES[PLAN_IDS.FREE];
};

// Check if a plan is a specific plan or higher
export const isPlanOrHigher = (currentPlanNameOrId, targetPlanId) => {
  const planOrder = [
    PLAN_IDS.FREE,
    PLAN_IDS.BASIC,
    PLAN_IDS.PRO, 
    PLAN_IDS.AGENCY,
    PLAN_IDS.ENTERPRISE
  ];
  
  // Convert plan name to ID if needed
  let currentPlanId = currentPlanNameOrId;
  if (!Object.values(PLAN_IDS).includes(currentPlanNameOrId)) {
    currentPlanId = getPlanIdFromName(currentPlanNameOrId);
  }
  
  const currentIndex = planOrder.indexOf(currentPlanId);
  const targetIndex = planOrder.indexOf(targetPlanId);
  
  return currentIndex >= targetIndex;
};

// Get plan ID from plan name
export const getPlanIdFromName = (planName) => {
  if (!planName) return PLAN_IDS.FREE;
  
  // Check if it's already a plan ID
  if (Object.values(PLAN_IDS).includes(planName)) {
    return planName;
  }
  
  // Convert name to ID
  for (const [id, name] of Object.entries(PLAN_NAMES)) {
    if (name.toLowerCase() === planName.toLowerCase()) {
      return id;
    }
  }
  
  // Default to free if not found
  return PLAN_IDS.FREE;
}; 